import { maxNameLength, rxName, rxNameMsg } from "src/common/rxName";
import { zSidLabel } from "src/types";
import { z } from "zod";

// Атрибуты объединены с описанием типов в один модуль.
// Иначе из-за взаимных ссылок возможна ситуация, когда происходит попытка обращения к модулю, который ещё не загружен.
import { ZAttrEdit, zAttrEdit } from "./ZAttrEdit";
import { ZAttrView, zAttrView } from "./ZAttrView";
import { zDateDefault } from "./ZDateDefault";
import { zStrOptionRole } from "./ZStrOptionRole";

const msgDefault = "Значение по умолчанию";

export const zAttrMetaList: z.ZodType<ZAttrMeta[]> = z.lazy(() =>
  zAttrMeta.array(),
);

export const zAttrType = z.enum([
  "string",
  "number",
  "boolean",
  "strOption",
  "dateTime",
  "file",
  "list",
  "array",
]);
export type ZAttrType = z.infer<typeof zAttrType>;

const zStringDef = z.object({
  name: z.literal(zAttrType.enum.string).describe("Тип данных: Текст"),
  maxLength: z
    .number()
    .int()
    .min(1)
    .optional()
    .describe("Максимально допустимое количество символов"),
  defaultValue: z.string().nullish().describe(msgDefault),
});
export type ZStringDef = z.infer<typeof zStringDef>;

export const zNumberDef = z.object({
  name: z.literal(zAttrType.enum.number).describe("Тип данных: Число"),
  precision: z
    .number()
    .int()
    .min(0)
    .max(10)
    .nullish()
    .describe("Точность (число цифр после запятой)"),
  defaultValue: z.number().nullish().describe(msgDefault),
});

export const zBoolDef = z.object({
  name: z.literal(zAttrType.enum.boolean).describe("Тип данных: Булевский"),
  defaultValue: z.boolean().nullish().describe(msgDefault),
});

export const zStrOptionDef = z.object({
  name: z
    .literal(zAttrType.enum.strOption)
    .describe("Тип данных: Строковая опция"),
  role: zStrOptionRole.optional().describe("На какой тип объектов указывает"),
  defaultValue: zSidLabel.nullish().describe(msgDefault),
});

export const zDateTimeDef = z.object({
  name: z.literal(zAttrType.enum.dateTime).describe("Тип данных: дата/время"),
  dateOnly: z.boolean().optional().describe("Только дата"),
  defaultValue: zDateDefault.nullish().describe(msgDefault),
});

export const zFileDef = z.object({
  name: z.literal(zAttrType.enum.file).describe("Тип данных: Файл"),
  image: z.boolean().optional(),
});

export const zListDef = z.object({
  name: z.literal(zAttrType.enum.list).describe("Тип данных: Список"),
  fields: zAttrMetaList
    .nullish()
    .describe("Поля, которые образуют каждый элемент списка"),
  maxCount: z
    .number()
    .int()
    .min(1)
    .nullish()
    .describe("Максимальное количество элементов в всписке"),
});
export type ZListDef = z.infer<typeof zListDef>;

type ZArrayItem = {
  type: ZTypeDef;
};

const zArrayItem: z.ZodType<ZArrayItem> = z.lazy(() =>
  z.object({
    type: zTypeDef,
  }),
);

const zArrayDef = z.object({
  name: z.literal(zAttrType.enum.array),
  item: zArrayItem.optional().describe("Описание элемента массива"),
  maxCount: z
    .number()
    .int()
    .min(1)
    .nullish()
    .describe("Максимальное количество элементов в массиве"),
});

export const zTypeDef = z.discriminatedUnion("name", [
  zStringDef,
  zNumberDef,
  zBoolDef,
  zDateTimeDef,
  zStrOptionDef,
  zFileDef,
  zListDef,
  zArrayDef,
]);
export type ZTypeDef = z.infer<typeof zTypeDef>;

// --- Атрибуты
// http://wiki.d4r.int/pages/viewpage.action?pageId=514856982

export type ZAttrMeta = {
  type: ZTypeDef;
  name: string;
  title: string;
  uid: string;
  displayId: number;
  required?: boolean | null;
  view?: ZAttrView | null;
  edit?: ZAttrEdit | null;
};

export const zAttrMeta: z.ZodType<ZAttrMeta> = z.lazy(() =>
  z.object({
    type: zTypeDef.describe("Тип данных"),
    name: z
      .string()
      .regex(rxName, { error: rxNameMsg() })
      .max(maxNameLength) // MDM2-359
      .describe("Системное наименование"),
    title: z
      .string()
      .max(maxNameLength)
      .describe("Локализованное наименование"),
    uid: z.string().describe("уникальный идентификатор (uuid)"),
    displayId: z.number().describe("числовой уникальный идентификатор"),
    required: z.boolean().nullish().describe("Обязательность"),
    view: zAttrView.nullish().describe("Свойства для просмотра"),
    edit: zAttrEdit.nullish().describe("Свойства для редактирования"),
  }),
);
